import { DocumentTextIcon } from "@heroicons/react/24/outline";
import { DropdownItem } from "../../shared-components/DropdownCombo";
import { PersonFormStateV2 as PersonFormState } from "../../api/Reports";
import Button, {
  ButtonColorTypes,
  ButtonTypes,
} from "../../shared-components/Button";
import LoadingSpinner from "../../shared-components/LoadingSpinner";
import ErrorMessageContainer from "../../shared-components/ErrorMessageContainer";
import { BlueprintMinimalInfo } from "../../api/Blueprints";
import { classNames } from "../../utils";

type Props = {
  blueprintItems: BlueprintMinimalInfo[];
  state: PersonFormState;
  behaviorKitItems: DropdownItem[];
  companyItems: DropdownItem[];
  isLoadingCreatePerson: boolean;
  close: () => void;
  handleSubmit: (event: any) => void;
  error: string | JSX.Element[];
};

export const PersonFormReview = ({
  blueprintItems,
  state,
  behaviorKitItems,
  companyItems,
  isLoadingCreatePerson,
  handleSubmit,
  close,
  error,
}: Props) => {
  return (
    <>
      <div className="flex items-center mb-5 gap-2">
        <DocumentTextIcon className="bg-blue-50 text-blue-700 rounded-full p-3 w-12"></DocumentTextIcon>
        <span className="font-semibold text-xl leading-7 text-gray-900">
          SUMMARY
        </span>
      </div>
      <div className="border p-5 text-gray-90 rounded-lg">
        <div className="flex">
          <div className="flex flex-col gap-3  flex-1">
            <div className="flex flex-col mb-4">
              <span className="text-gray-400">Screening Company & Title:</span>
              <b className="font-semibold">
                {
                  companyItems.find((item) => item.id === state.company_uuid)
                    ?.label
                }
                <br />
                {state.job_title}
              </b>
            </div>
            <div className="flex flex-col">
              <span className="text-gray-400">Check Type:</span>
              <b className="font-semibold">
                {blueprintItems.map((item) => (
                  <div>{item.name}</div>
                ))}
              </b>
            </div>
            <div className="flex flex-col ">
              <span className="text-gray-400">Behavior Kit:</span>
              <b className="font-semibold">
                {
                  behaviorKitItems.find(
                    (kit) => kit.id === state.behavior_kit_uuid
                  )?.label
                }
              </b>
            </div>
            <div className="flex flex-col ">
              <span className="text-gray-400">
                Name{state.names.length > 1 && "s"}:
              </span>
              {state.names.map((name) => (
                <b className="flex flex-col">
                  {name.first_name} {name.middle_name} {name.last_name}
                </b>
              ))}
            </div>
            <div className="flex flex-col ">
              <span className="text-gray-400">Email:</span>
              {state.emails.map((email) => (
                <span className="flex font-semibold flex-col">
                  {email.address}
                </span>
              ))}
            </div>
            <div className="flex flex-col ">
              <span className="text-gray-400">
                Phone{state.phones.length > 1 && "s"}:
              </span>
              {state.phones.map((phone) => (
                <span className="flex font-semibold flex-col">
                  {phone.raw_number}
                </span>
              ))}
            </div>
          </div>
          <div className="flex flex-col gap-3 flex-1">
            <div className="flex flex-col ">
              <span className="text-gray-400">Birthday:</span>
              <span className="flex font-semibold flex-col">
                {/* Regex replaces dashes for slashes as per design */}
                {state.birth_date.replace(/-/g, "/")}
              </span>
            </div>
            <div className="flex flex-col ">
              <span className="text-gray-400">Social Media:</span>
              {state.profiles.map((profile) => (
                <span className="flex  font-semibold flex-col">{profile}</span>
              ))}
            </div>
            <div className="flex flex-col ">
              <span className="text-gray-400">
                Address{state.addresses.length > 1 && "s"}:
              </span>
              {state.addresses.map((addresses, idx) => (
                <span
                  className={classNames(
                    "flex font-semibold flex-col",
                    idx > 0 ? "mt-3" : ""
                  )}
                >
                  {addresses.line1}
                  <br />
                  {addresses.city} {addresses.state} {addresses.zip}
                </span>
              ))}
            </div>
            <div className="flex flex-col ">
              <span className="text-gray-400">Education:</span>
              {state.degrees.map((school, idx) => (
                <span
                  className={classNames(
                    "flex font-semibold flex-col",
                    idx > 0 ? "mt-3" : ""
                  )}
                >
                  {school.name}
                  <br />
                  {school.educator}
                </span>
              ))}
            </div>
            <div className="flex flex-col ">
              <span className="text-gray-400">Employment:</span>
              {state.jobs.map((job, idx) => (
                <span
                  className={classNames(
                    "flex font-semibold flex-col",
                    idx > 0 ? "mt-3" : ""
                  )}
                >
                  {job.title}
                  <br />
                  {job.employer}
                </span>
              ))}
            </div>
            {(state.client_qa || state.internal_qa) && (
              <div className="flex flex-col">
                <span className="text-gray-400">Investigator:</span>
                {state.client_qa && (
                  <span className="flex font-semibold flex-col">
                    Client Investigator: yes
                  </span>
                )}
                {state.internal_qa && (
                  <span className="flex font-semibold flex-col mt-3">
                    Internal Investigator: yes
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="flex mt-5 text-gray-700 text-sm flex-col gap-5">
        <p>
          By clicking the "Submit" button below, you confirm that the provided
          information is accurate and complete.
        </p>

        {error && (
          <ErrorMessageContainer>
            <>{error}</>
          </ErrorMessageContainer>
        )}
        <div className="flex gap-1 justify-end">
          <Button
            type={ButtonTypes.default}
            text="Cancel"
            colorType={ButtonColorTypes.cancel}
            onClick={close}
            isRoundedFull={true}
            className="w-24"
          />
          <Button
            type={ButtonTypes.default}
            disabled={isLoadingCreatePerson}
            text={isLoadingCreatePerson ? <LoadingSpinner /> : "Submit"}
            colorType={ButtonColorTypes.success}
            isRoundedFull={true}
            className="bg-orange-500 w-32"
            onClick={handleSubmit}
          />
        </div>
      </div>
    </>
  );
};
